import { useEffect } from 'react';

/**
 * Custom hook to force scroll to top with multiple fallback mechanisms
 * Ensures the page always scrolls to top regardless of timing or lazy loading
 */
export function useScrollToTop() {
  const scrollToTop = () => {
    // Multiple methods to ensure scroll works in all browsers
    window.scrollTo({ top: 0, behavior: 'auto' });
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    // Safari iOS fix
    if (document.scrollingElement) {
      document.scrollingElement.scrollTop = 0;
    }
  };

  useEffect(() => {
    // Immediate scroll
    scrollToTop();

    // Using requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      scrollToTop();
    });

    // Additional timeouts as failsafe for lazy-loaded content
    const timers = [
      setTimeout(scrollToTop, 0),
      setTimeout(scrollToTop, 10),
      setTimeout(scrollToTop, 50),
      setTimeout(scrollToTop, 100),
    ];

    return () => {
      timers.forEach((timer) => clearTimeout(timer));
    };
  }, []);
}

/**
 * Utility function to force immediate scroll to top
 * Can be called from anywhere in the app
 */
export function forceScrollToTop() {
  // Multiple methods for cross-browser compatibility
  window.scrollTo({ top: 0, behavior: 'auto' });
  window.scrollTo(0, 0);
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;

  if (document.scrollingElement) {
    document.scrollingElement.scrollTop = 0;
  }

  // Force repaint
  requestAnimationFrame(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  });
}
